import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';

import {
  DELETE_ANALYTICS_CARD,
  GET_ANALYTICS_CARD,
  UPDATE_ANALYTICS_CARD,
} from '@atom/graph/analytics';
import { useNavigation } from '@atom/hooks/useNavigation';
import { Progress, Snackbar } from '@atom/mui';
import {
  AnalyticsCardUpdateInput,
  AnalyticsModal,
} from '@atom/types/analytics';

import EditAnalyticsCardModal from '../analyticsDashboard/analyticsModals/EditAnalyticsCardModal';
import DeleteModal from '../common/DeleteModal';

import AnalyticsHeader from './AnalyticsHeader';
import AnalyticsTile, { tileSizeClass } from './AnalyticsTile';

import classes from './analyticsDetail.module.css';

const AnalyticsDetail = () => {
  const [, { navigate, params }] = useNavigation();
  const { id } = params;
  const [activeModal, setActiveModal] = useState<AnalyticsModal>(null);

  const { data, loading, refetch } = useQuery(GET_ANALYTICS_CARD, {
    variables: {
      id,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
  });

  const [updateCard] = useMutation(UPDATE_ANALYTICS_CARD);

  const handleUpdateCard = async (input: AnalyticsCardUpdateInput) => {
    try {
      await updateCard({
        variables: {
          input,
        },
      });
      Snackbar.info({
        message: 'Updated.',
      });
      refetch();
    } catch (error) {
      Snackbar.error({
        message: `An error occurred while updating "${data?.analyticsCard?.name}". Please Try Again.`,
      });
    } finally {
      setActiveModal(null);
    }
  };

  const [deleteCard] = useMutation(DELETE_ANALYTICS_CARD);

  const handleDeleteCard = async () => {
    try {
      await deleteCard({ variables: { id } });
      navigate('/analytics/dashboard');
    } catch (error) {
      Snackbar.error({
        message: `An error occurred". Please Try Again.`,
      });
    }
  };

  const tileSize =
    data?.analyticsCard?.embedLinks.length === 1 ? 'large' : 'normal';
  return (
    <>
      <AnalyticsHeader
        name={data?.analyticsCard?.name || ''}
        setActiveModal={setActiveModal}
        loading={loading}
      />
      <div className={classes.analyticsPortalContent}>
        {loading ? (
          <div className={classes.loadingContainer}>
            <Progress />
          </div>
        ) : (
          <>
            <div
              className={`${classes.analyticsTiles} ${tileSizeClass[tileSize]}`}
            >
              {data?.analyticsCard?.embedLinks.map(
                (url: string, index: number) => {
                  return (
                    <AnalyticsTile key={index} url={url} tileSize={tileSize} />
                  );
                },
              )}
            </div>
            <EditAnalyticsCardModal
              open={activeModal === AnalyticsModal.UPDATE}
              onClose={() => setActiveModal(null)}
              handleUpdateCard={handleUpdateCard}
              card={data?.analyticsCard}
            />
            <DeleteModal
              open={activeModal === AnalyticsModal.DELETE}
              title="Delete Card"
              content="Are you sure you want to delete this card?"
              onCancel={() => setActiveModal(null)}
              onConfirm={handleDeleteCard}
            />
          </>
        )}
      </div>
    </>
  );
};

export default AnalyticsDetail;
